import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../hooks/useAuth';
import Icon from '../components/Icons';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';

const DIAS_SEMANA = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const POLL_MS = 30000;

const EVENT_LABELS = {
  cotizacion:   'Cotización',
  inicio:       'Inicio obra',
  fin_estimado: ' Fin estimado',
  fin_real:     'Fin real',
  cita:         'Cita',
  obra:         'Obra',
};

export default function AgendaPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGerencia = ['admin','master','gerente_financiera','gerente_proyectos'].includes(user?.rol);
  const isTecnico = user?.rol === 'tecnico';
  const [view, setView] = useState('month'); // 'month' | 'week'
  const [current, setCurrent] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState(null);
  const [filterUser, setFilterUser] = useState('');
  const [filterType, setFilterType] = useState('');
  const [users, setUsers] = useState([]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      // Calcular rango visible
      const year = current.getFullYear();
      const month = current.getMonth();
      const start = new Date(year, month - 1, 1).toISOString().split('T')[0];
      const end = new Date(year, month + 2, 0).toISOString().split('T')[0];
      const res = await api.get('/calendar', { params: { start, end } });
      setEvents(res.data);

      // Extraer usuarios únicos
      const uniqueUsers = [];
      const seen = new Set();
      for (const ev of res.data) {
        if (!seen.has(ev.userId)) {
          seen.add(ev.userId);
          uniqueUsers.push({ id: ev.userId, name: ev.userName });
        }
      }
      setUsers(uniqueUsers);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [current]);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  // Refresco automático para reflejar cambios (entrada/salida de obra, etc.) sin recargar la página
  useEffect(() => {
    const interval = setInterval(loadEvents, POLL_MS);
    return () => clearInterval(interval);
  }, [loadEvents]);

  const canOpen = (ev) => {
    if (isGerencia) return true;
    if (isTecnico) return ev.category === 'obra' && ev.userId === user?.id;
    if (ev.category === 'obra') return ev.asesorId === user?.id;
    return ev.userId === user?.id;
  };

  const handleEventClick = (ev) => {
    if (!canOpen(ev)) return;
    if (ev.category === 'cita') { navigate('/citas', { state: { openCitaId: ev.appointmentId } }); return; }
    if (ev.category === 'obra') {
      if (isGerencia || isTecnico) { navigate('/obra', { state: { openObraId: ev.obraId } }); return; }
      navigate('/proyectos', { state: { openProyectoId: ev.proyectoId } }); // asesor no tiene acceso a /obra
      return;
    }
    navigate('/proyectos', { state: { openProyectoId: ev.proyectoId } }); // categoría 'proyecto'
  };

  const filteredEvents = events.filter(ev => {
    if (filterUser && ev.userId !== filterUser) return false;
    if (filterType && ev.type !== filterType) return false;
    return true;
  });

  const getEventsForDate = (dateStr) =>
    filteredEvents.filter(ev => ev.date === dateStr);

  // ── MONTH VIEW ────────────────────────────────────────────────────────────
  const renderMonth = () => {
    const year = current.getFullYear();
    const month = current.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date().toISOString().split('T')[0];

    const cells = [];
    // Empty cells before month start
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
      <div>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, marginBottom: 1 }}>
          {DIAS_SEMANA.map(d => (
            <div key={d} style={{ textAlign: 'center', padding: '8px 4px', fontSize: 12, fontWeight: 700, color: '#888', background: 'white', textTransform: 'uppercase', letterSpacing: 0.5 }}>{d}</div>
          ))}
        </div>
        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, background: '#e5e5e5' }}>
          {cells.map((day, idx) => {
            if (!day) return <div key={`empty-${idx}`} style={{ background: '#fafafa', minHeight: 110 }} />;
            const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const dayEvents = getEventsForDate(dateStr);
            const isToday = dateStr === today;
            const isWeekend = [0, 6].includes((new Date(year, month, day)).getDay());

            return (
              <div key={dateStr} style={{ background: isWeekend ? '#fdfafa' : 'white', minHeight: 110, padding: '6px 4px', position: 'relative' }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: isToday ? '#7B0D0D' : 'transparent',
                  color: isToday ? 'white' : isWeekend ? '#aaa' : '#333',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: isToday ? 700 : 400, marginBottom: 4
                }}>{day}</div>

                {dayEvents.slice(0, 3).map(ev => (
                  <div key={ev.id}
                    onMouseEnter={e => setTooltip({ ev, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setTooltip(null)}
                    onClick={() => handleEventClick(ev)}
                    style={{
                      background: ev.color + '22',
                      borderLeft: `3px solid ${ev.color}`,
                      color: ev.color,
                      fontSize: 11, fontWeight: 500,
                      padding: '2px 5px', borderRadius: '0 4px 4px 0',
                      marginBottom: 2, cursor: canOpen(ev) ? 'pointer' : 'default',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>
                    {ev.title.length > 22 ? ev.title.slice(0, 22) + '…' : ev.title}
                  </div>
                ))}
                {dayEvents.length > 3 && (
                  <div style={{ fontSize: 11, color: '#888', paddingLeft: 4 }}>+{dayEvents.length - 3} más</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // ── WEEK VIEW ─────────────────────────────────────────────────────────────
  const renderWeek = () => {
    const startOfWeek = new Date(current);
    const day = startOfWeek.getDay();
    startOfWeek.setDate(startOfWeek.getDate() - day);

    const days = Array.from({ length: 7 }, (_, i) => {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      return d;
    });

    const today = new Date().toISOString().split('T')[0];

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, background: '#e5e5e5' }}>
        {days.map(d => {
          const dateStr = d.toISOString().split('T')[0];
          const dayEvents = getEventsForDate(dateStr);
          const isToday = dateStr === today;
          const isWeekend = [0, 6].includes(d.getDay());

          return (
            <div key={dateStr} style={{ background: isWeekend ? '#fdfafa' : 'white', minHeight: 300, padding: '8px 6px' }}>
              <div style={{ textAlign: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 11, color: '#888', textTransform: 'uppercase', fontWeight: 600 }}>{DIAS_SEMANA[d.getDay()]}</div>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', margin: '4px auto 0',
                  background: isToday ? '#7B0D0D' : 'transparent',
                  color: isToday ? 'white' : '#333',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, fontWeight: 600
                }}>{d.getDate()}</div>
              </div>
              {dayEvents.map(ev => (
                <div key={ev.id}
                  onMouseEnter={e => setTooltip({ ev, x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setTooltip(null)}
                  onClick={() => handleEventClick(ev)}
                  style={{
                    background: ev.color + '18',
                    border: `1px solid ${ev.color}44`,
                    borderLeft: `3px solid ${ev.color}`,
                    color: '#333', fontSize: 11,
                    padding: '4px 6px', borderRadius: '0 6px 6px 0',
                    marginBottom: 4, cursor: canOpen(ev) ? 'pointer' : 'default',
                  }}>
                  <div style={{ fontWeight: 600, color: ev.color, marginBottom: 1 }}>{EVENT_LABELS[ev.type] || ev.type}</div>
                  <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{ev.empresa}</div>
                  <div style={{ fontSize: 10, color: '#888' }}>{ev.userName}</div>
                  {ev.time && <div style={{ fontSize: 10, color: '#666' }}> {ev.time.slice(0,5)}</div>}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  };

  const stepDate = (dir) => {
    const d = new Date(current);
    if (view === 'month') d.setMonth(d.getMonth() + dir);
    else d.setDate(d.getDate() + (dir * 7));
    setCurrent(d);
  };

  const title = view === 'month'
    ? `${MESES[current.getMonth()]} ${current.getFullYear()}`
    : (() => {
        const start = new Date(current);
        start.setDate(start.getDate() - start.getDay());
        const end = new Date(start); end.setDate(start.getDate() + 6);
        return `${start.getDate()} ${MESES[start.getMonth()]} — ${end.getDate()} ${MESES[end.getMonth()]} ${end.getFullYear()}`;
      })();

  // Usuarios presentes en el rango visible, para la leyenda
  const eventCounts = {};
  for (const ev of filteredEvents) {
    if (!eventCounts[ev.userId]) eventCounts[ev.userId] = { name: ev.userName, color: ev.color };
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Agenda compartida</h1>
          <p className="page-subtitle">Visualiza todas las actividades del equipo</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ display: 'flex', background: 'white', borderRadius: 8, border: '1px solid #e5e5e5', overflow: 'hidden' }}>
            {['month', 'week'].map(v => (
              <button key={v} onClick={() => setView(v)}
                style={{ padding: '8px 16px', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 500, background: view === v ? '#7B0D0D' : 'white', color: view === v ? 'white' : '#666', transition: 'all 0.2s' }}>
                {v === 'month' ? ' Mes' : ' Semana'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filters-bar" style={{ marginBottom: 16 }}>
        <select className="form-select" style={{ width: 200 }} value={filterUser} onChange={e => setFilterUser(e.target.value)}>
          <option value="">Todos los asesores</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <select className="form-select" style={{ width: 180 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Todos los tipos</option>
          <option value="cita"> Citas</option>
          <option value="cotizacion"> Cotizaciones</option>
          <option value="inicio"> Inicio de obra</option>
          <option value="fin_estimado"> Fin estimado</option>
          <option value="fin_real"> Fin real</option>
          <option value="obra"> Obra</option>
        </select>
        {(filterUser || filterType) && (
          <button className="btn btn-secondary btn-sm" onClick={() => { setFilterUser(''); setFilterType(''); }}>Limpiar</button>
        )}
      </div>

      {/* Leyenda de usuarios */}
      {Object.values(eventCounts).length > 0 && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
          {Object.values(eventCounts).map(u => (
            <div key={u.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: u.color, flexShrink: 0 }} />
              <span style={{ color: '#555' }}>{u.name}</span>
            </div>
          ))}
        </div>
      )}

      {/* Calendario */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {/* Header navegación */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #e5e5e5', background: 'white' }}>
          <button className="btn-icon" onClick={() => stepDate(-1)}><ChevronLeft size={20} /></button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h2 style={{ fontSize: 17, fontWeight: 700 }}>{title}</h2>
            {loading && <div className="spinner spinner-dark" style={{ width: 16, height: 16 }} />}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setCurrent(new Date())}>Hoy</button>
            <button className="btn-icon" onClick={() => stepDate(1)}><ChevronRight size={20} /></button>
          </div>
        </div>

        {/* Contenido */}
        {view === 'month' ? renderMonth() : renderWeek()}
      </div>

      {/* Leyenda de tipos */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 16, padding: '12px 16px', background: 'white', borderRadius: 10, boxShadow: 'var(--sombra)' }}>
        <span style={{ fontSize: 12, color: '#888', fontWeight: 600 }}>TIPOS:</span>
        {Object.entries(EVENT_LABELS).map(([type, label]) => (
          <span key={type} style={{ fontSize: 12, color: '#555' }}>{label}</span>
        ))}
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div style={{
          position: 'fixed', left: tooltip.x + 12, top: tooltip.y - 10, zIndex: 9999,
          background: '#1a1a1a', color: 'white', borderRadius: 8, padding: '10px 14px',
          fontSize: 13, maxWidth: 320, boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
          pointerEvents: 'none', lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 700, color: tooltip.ev.color, marginBottom: 4 }}>{tooltip.ev.title}</div>
          {tooltip.ev.empresa && <div>{tooltip.ev.empresa}</div>}
          {tooltip.ev.time && <div> {tooltip.ev.time.slice(0,5)}</div>}
          {tooltip.ev.tema && <div> {tooltip.ev.tema}</div>}
          {tooltip.ev.contactoNombre && <div> Contacto: {tooltip.ev.contactoNombre}{tooltip.ev.contactoTelefono ? ` (${tooltip.ev.contactoTelefono})` : ''}</div>}
          {tooltip.ev.direccion && <div> {tooltip.ev.direccion}</div>}
          {tooltip.ev.invitadoNombre
            ? <div style={{ color: '#fbbf24' }}> Va acompañado de: {tooltip.ev.invitadoNombre}</div>
            : tooltip.ev.category === 'cita' && <div style={{ color: '#888' }}> Sin acompañante</div>}
          <div style={{ marginTop: 4, color: '#aaa', fontSize: 12 }}>{tooltip.ev.userName}</div>
        </div>
      )}
    </div>
  );
}
